package nl.volleyplanner.web.service

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import nl.volleyplanner.api.User
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.client.VolleyPlannerAuthTokenLoader
import nl.volleyplanner.api.entities.Organisation
import nl.volleyplanner.api.entities.OrganisationSeason
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.routing.NavigationCause
import nl.volleyplanner.web.base.routing.NavigationManager


class OrganisationContext(
    val organisation: Organisation,
    val coroutineScope: CoroutineScope,
    val apiAuthTokenLoader: VolleyPlannerAuthTokenLoader = VolleyPlannerWebAuthTokenLoader
) {
    val volleyplannerApiClient = VolleyplannerApiClientFactory.createVolleyplannerApiClient(organisation.identifier, apiAuthTokenLoader)
    val volleyplannerApiPlannerClient = VolleyplannerApiClientFactory.createVolleyplannerApiPlannerClient(organisation.identifier, apiAuthTokenLoader)
    val volleyplannerApiFileUploadClient = VolleyplannerApiClientFactory.createVolleyplannerApiFileUploadClient(organisation.identifier, apiAuthTokenLoader)

    val availableSeasons = ApiDataStateFlow(
        "OrganisationContext-availableSeasons",
        AuthLevel.AnonymousPublic,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope) {
        volleyplannerApiClient.organisation.getSeasons()
    }

    val activeSeason: StateFlow<OrganisationSeason?> = availableSeasons.subscribe()
        .filterNotNull()
        .map { seasons ->
            seasons.firstOrNull { it.active }
        }
        .stateIn(coroutineScope, SharingStarted.WhileSubscribed(5000), null)
}

object WebUserAndOrganisationService {
    private val logger = Logger.getLogger("nl.volleyplanner.web.service.UserAndOrganisationService")
    private val COROUTINE_SCOPE = CoroutineManager.newServiceScope("UserAndOrganisationService")

    val userProfileState = MutableStateFlow<User?>(null)
    val availableOrganisationsState = MutableStateFlow<List<Organisation>?>(null)
    val selectedOrganisationState = MutableStateFlow<OrganisationContext?>(null)

    suspend fun refreshAvailableOrganisations() {
        val organisations = VolleyPlannerApplication.volleyplannerRootApiClient.getOrganisations()
        availableOrganisationsState.value = organisations
    }

    suspend fun refreshUserProfile() {
        val userProfile = VolleyPlannerApplication.volleyplannerRootApiClient.getUserProfile()
        userProfileState.value = userProfile

        // TODO: Check if this can be removed, should no longer be necessary
        AuthContextService.setDbUser(userProfile)
    }

    fun initialize() {
        COROUTINE_SCOPE.launch {
            AuthContextService.authUserState.collectLatest { authUser ->
                logger.info { "AuthUser changed: $authUser" }
                // Received a new auth user
                val previousOrganisationContext = selectedOrganisationState.value
                userProfileState.value = null
                availableOrganisationsState.value = null
                selectedOrganisationState.value = null

                if ((authUser?.authLevel ?: AuthLevel.AnonymousPublic) >= AuthLevel.BasicUser) {
                    launch {
                        val organisations = VolleyPlannerApplication.volleyplannerRootApiClient.getOrganisations()
                        availableOrganisationsState.value = organisations

                        val selectedOrganisation = if (previousOrganisationContext != null && organisations.contains(
                                previousOrganisationContext.organisation
                            )
                        ) {
                            previousOrganisationContext.organisation
                        } else organisations.firstOrNull()

                        selectedOrganisationState.value =
                            selectedOrganisation?.let { OrganisationContext(selectedOrganisation, COROUTINE_SCOPE) }
                    }
                    launch {
                        refreshUserProfile()
                    }
                }
            }
        }
    }

    fun changeActiveOrganisation(newOrganisation: Organisation) {
        if (availableOrganisationsState.value == null || !availableOrganisationsState.value!!.contains(newOrganisation)) {
            throw IllegalArgumentException("Organisation $newOrganisation is not available")
        }

        selectedOrganisationState.value = OrganisationContext(newOrganisation, COROUTINE_SCOPE)

        NavigationManager.navigateTo("/planner", NavigationCause.Programmatic)
    }
}
